import { Link } from "react-router-dom";
import { ArrowRight, Shield, Sliders, Gem, Box } from "lucide-react";
import { KnovasMark } from "../components/KnovasLogo";
import Seo from "../components/Seo";

const BRAND_IMG = "https://customer-assets.emergentagent.com/job_knovas-catalog/artifacts/0n7g8nhf_knovas_instagram_final.jpg";

export default function Cover() {
  return (
    <>
    <Seo
      title="KNOVAS · Premium Modular Kitchen & Wardrobe Hardware India"
      description="KNOVAS engineers premium kitchen and wardrobe hardware for modular kitchens across India — hinges, drawer slides, tandem boxes, pantry systems, magic corners, kitchen baskets and more. Built strong. Built to last."
      path="/"
    />
    <section data-testid="cover-page" className="relative min-h-screen bg-[#2B2B2B] text-white overflow-hidden">
      {/* Background texture */}
      <div
        className="absolute inset-0 opacity-[0.18] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            'url("https://images.unsplash.com/photo-1617225419138-fa15387123fd?auto=format&fit=crop&q=80&w=2000")',
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        aria-hidden
      />
      {/* Vertical orange strip */}
      <div className="absolute top-0 bottom-0 left-0 w-2 md:w-4 bg-[#FF6A00] z-10" aria-hidden />

      {/* Horizontal accent strip */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-[#FF6A00]/60 z-10" aria-hidden />

      <div className="relative z-20 max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 pt-32 pb-32 min-h-screen flex flex-col justify-between">
        {/* Top label */}
        <div className="flex items-center justify-between text-[10px] sm:text-xs tracking-[0.4em] uppercase text-white/50">
          <span data-testid="cover-edition">KNOVAS · CATALOGUE EDITION 2025</span>
          <span className="hidden sm:inline">VOL. 01 / 25 SERIES</span>
        </div>

        {/* Center wordmark */}
        <div className="flex-1 flex flex-col justify-center py-16">
          <p className="text-xs sm:text-sm tracking-[0.5em] uppercase text-[#FF6A00] mb-8">Premium Hardware</p>

          <h1
            data-testid="cover-title"
            className="font-black uppercase leading-[0.85] tracking-[-0.04em] text-[18vw] md:text-[16vw] lg:text-[14vw]"
            style={{ fontFamily: "'Anton', sans-serif" }}
          >
            <span className="text-black">K</span><span className="text-[#FF6A00]">N</span><span className="text-white">OVAS</span>
          </h1>
          <div className="mt-8 max-w-2xl border-l-2 border-[#FF6A00] pl-6">
            <p className="text-lg sm:text-xl text-white/80 leading-relaxed">
              The definitive engineering catalogue for kitchen, wardrobe and modern interior hardware —
              25 series, hundreds of components, one uncompromising standard.
            </p>
          </div>
        </div>

        {/* Tagline + CTA */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-end">
          <div>
            <p className="text-xs tracking-[0.4em] uppercase text-white/40 mb-3">Tagline</p>
            <p
              data-testid="cover-tagline"
              className="text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight leading-[1.05]"
              style={{ fontFamily: "'Anton', sans-serif", letterSpacing: "0.02em" }}
            >
              Built Strong.<br />
              <span className="text-[#FF6A00]">Built to Last.</span>
            </p>
          </div>

          <div className="flex flex-col sm:flex-row md:justify-end gap-4">
            <Link
              to="/catalogue"
              data-testid="cover-cta-catalogue"
              className="group inline-flex items-center justify-between gap-6 bg-[#FF6A00] hover:bg-[#E65C00] text-white px-8 py-5 text-xs tracking-[0.3em] uppercase font-bold transition-colors"
            >
              <span>Open Catalogue</span>
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/series/nsb-slim-box"
              data-testid="cover-cta-featured"
              className="inline-flex items-center justify-between gap-6 border border-white/30 hover:border-[#FF6A00] hover:text-[#FF6A00] text-white px-8 py-5 text-xs tracking-[0.3em] uppercase font-bold transition-colors"
            >
              <span>Featured: NSB Slim</span>
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* BRAND THEME BANNER */}
    <section data-testid="brand-theme-section" className="relative bg-white">
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 pt-20 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <p className="text-xs tracking-[0.4em] uppercase text-[#FF6A00] mb-4">Brand Theme</p>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-[#2B2B2B] leading-[0.95]"
              style={{ fontFamily: "'Anton', sans-serif" }}
            >
              Designed to work.<br />
              <span className="text-[#FF6A00]">Built to last.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base text-[#4B5563] leading-relaxed border-l-2 border-[#FF6A00] pl-6">
              KNOVAS hardware solutions for every space, every home —
              engineered with <span className="font-semibold text-[#2B2B2B]">50 kg or more capacity</span>, smooth motion and a premium finish.
            </p>
          </div>
        </div>
      </div>

      {/* Full bleed brand image */}
      <div className="relative bg-[#F3F4F6]">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="relative overflow-hidden border border-[#E5E7EB] bg-white">
            <img
              src={BRAND_IMG}
              alt="KNOVAS — Designed to work. Built to last."
              data-testid="brand-theme-image"
              className="w-full h-auto block"
              loading="lazy"
            />
          </div>
        </div>

        {/* Pillar strip */}
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#E5E7EB]">
            {[
              { icon: Shield,  title: "Strong & Durable",  copy: "50 kg+ load rated. Engineered for daily abuse." },
              { icon: Sliders, title: "Smooth Performance", copy: "Soft-close motion. Silent, repeatable, calibrated." },
              { icon: Gem,     title: "Premium Finish",    copy: "European-grade surfacing. Fingerprint resistant." },
              { icon: Box,     title: "Smart Design",      copy: "Universal mounting. Faster installation." },
            ].map((p, i) => (
              <div key={i} className="bg-white p-8 hover:bg-[#F9FAFB] transition-colors">
                <p.icon size={22} className="text-[#FF6A00] mb-5" />
                <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-[#2B2B2B]">{p.title}</h3>
                <p className="mt-3 text-sm text-[#6B7280] leading-relaxed">{p.copy}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA strip */}
        <div className="bg-[#2B2B2B] text-white">
          <div className="h-1 w-full bg-[#FF6A00]" aria-hidden />
          <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 py-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <p className="text-xs tracking-[0.4em] uppercase text-white/40 mb-3">Every Space · Every Home</p>
              <p
                className="text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight"
                style={{ fontFamily: "'Anton', sans-serif" }}
              >
                Explore <span className="text-[#FF6A00]">25 engineered series</span> — built around one uncompromising standard.
              </p>
            </div>
            <div className="md:col-span-4 flex md:justify-end">
              <Link
                to="/catalogue"
                data-testid="brand-theme-cta"
                className="group inline-flex items-center justify-between gap-6 bg-[#FF6A00] hover:bg-[#E65C00] text-white px-8 py-5 text-xs tracking-[0.3em] uppercase font-bold transition-colors"
              >
                <span>Browse Catalogue</span>
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
    </>
  );
}
